import { createContext, type DOMAttributes, useCallback, useEffect, useMemo, useRef } from 'react';
import { type AriaRadioProps, useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupProps, type RadioGroupState, useRadioGroupState } from 'react-stately';
import cn from 'classnames';
import format from 'date-fns/format';
import isYesterday from 'date-fns/isYesterday';
import ru from 'date-fns/locale/ru';
import { HStack, Text, VStack } from '@direct-frontend/components';
import { entries, type Nullable } from '@direct-frontend/stdlib';
import { useField, useFormInstance } from '@adv-frontend/form-kit';
import { OrderDeliveryMethods, type ServiceType } from '@/shared/api/order';
import { DrawerBottomFixed, DrawerButton, DrawerHeader } from '@/shared/components/Drawer';
import { type IntervalFieldValue } from '@/shared/components/IntervalInput';
import { groupIntervalsByDate } from '@/shared/lib/datetime';
import { useRadioState } from '@/shared/lib/hooks';
import { isElementInParentViewport } from '@/shared/lib/isElementInParentViewport';
import type { OzonDeliveryInterval } from '../../../../../lib';
import { deliveryIntervalForm } from '../model/deliveryIntervalForm';
import { TimeSlotsRadioGroup } from './TimeSlotsRadioGroup';
import styles from './DeliveryIntervalForm.module.css';

const RadioContext = createContext<RadioGroupState | null>(null);

const DateOptionBox = (props: AriaRadioProps) => {
    const refInput = useRef<HTMLInputElement>(null);
    const refWrapper = useRef<HTMLDivElement>(null);
    const state = useRadioState(RadioContext);
    const { value } = props;
    const { inputProps, labelProps } = useRadio(props, state, refInput);
    const date = useMemo(() => new Date(value), [value]);

    const title = format(date, 'd MMM', { locale: ru }).replace('.', '');

    const subtitle = useMemo(() => {
        if (isYesterday(date)) {
            return 'Завтра';
        }

        return format(date, 'EEEEEE', { locale: ru });
    }, [date]);

    useEffect(() => {
        if (!inputProps.checked) {
            return;
        }

        const node = refWrapper.current;

        if (node && !isElementInParentViewport(node)) {
            node.scrollIntoView({ behavior: 'smooth' });
        }
    }, [inputProps.checked]);

    return (
        <VStack ref={refWrapper} className={cn(styles.option, inputProps.checked && styles.option__checked)}>
            <label {...labelProps} className={styles.optionInner}>
                <input {...inputProps} ref={refInput} className={styles.optionInput} />
                <VStack gap="2" alignItems="center" justifyContent="center" className={styles.optionTexts}>
                    <Text size="mc" role="main">
                        {title}
                    </Text>
                    <Text size="mc" role="supplementary" className={styles.optionSubtitle}>
                        {subtitle}
                    </Text>
                </VStack>
            </label>
        </VStack>
    );
};

type Interval = {
    from: number;
    to: number;
    serviceType: ServiceType;
    price: Nullable<string>;
    currency: Nullable<string>;
};

interface CourierTimeSlotPickerProps {
    radioGroupProps: DOMAttributes<HTMLElement>;
    radioGroupState: RadioGroupState;
    dates: Record<string, Interval[]>;
    formId: string;
    maxIntervals: number;
    ozonDeliveryInterval: OzonDeliveryInterval;
}

const CourierTimeSlotPicker = (props: CourierTimeSlotPickerProps) => {
    const { radioGroupState, dates, formId, maxIntervals, radioGroupProps, ozonDeliveryInterval } = props;

    return (
        <VStack className={styles.scroll}>
            <DrawerHeader className={styles.header} title="Дата и время доставки" />

            <HStack gap="8" {...radioGroupProps} className={styles.radioGroup}>
                <RadioContext.Provider value={radioGroupState}>
                    {Object.keys(dates).map((date, index) => (
                        <DateOptionBox key={index} value={date} aria-label="date" />
                    ))}
                </RadioContext.Provider>
            </HStack>
            {radioGroupState.selectedValue ? (
                <TimeSlotsRadioGroup
                    formId={formId}
                    intervals={dates[radioGroupState.selectedValue]}
                    maxIntervals={maxIntervals}
                    deliveryMethod={OrderDeliveryMethods.COURIER}
                    ozonDeliveryInterval={ozonDeliveryInterval}
                />
            ) : null}
        </VStack>
    );
};

interface PickupPointTimeSlotPickerProps {
    radioGroupState: RadioGroupState;
    formId: string;
    intervals: Interval[];
}

const PickupPointTimeSlotPicker = (props: PickupPointTimeSlotPickerProps) => {
    const { radioGroupState, formId, intervals } = props;

    return (
        <VStack className={styles.scroll}>
            <DrawerHeader className={styles.header} title="Дата доставки" />

            {radioGroupState.selectedValue ? (
                <TimeSlotsRadioGroup
                    deliveryMethod={OrderDeliveryMethods.PICKUP_POINT}
                    formId={formId}
                    intervals={intervals}
                />
            ) : null}
        </VStack>
    );
};

interface DeliveryIntervalFormProps {
    formId: string;
    onSubmit?: (interval: IntervalFieldValue) => void;
    intervals: Interval[];
    deliveryMethod: Nullable<string>;
    ozonDeliveryInterval: OzonDeliveryInterval;
}

export const DeliveryIntervalForm = (props: DeliveryIntervalFormProps) => {
    const { formId, intervals, deliveryMethod, onSubmit: propOnSubmit, ozonDeliveryInterval } = props;
    const formInstance = useFormInstance({ form: deliveryIntervalForm, id: formId });
    const { fields, revalidate } = formInstance;

    const { value: activeInterval } = useField(fields.deliveryInterval);

    const dates = useMemo(() => groupIntervalsByDate(intervals), [intervals]);

    const defaultDate = useMemo(() => {
        if (activeInterval.type === 'notInterval') {
            return null;
        }

        const activeIntervalDate = new Date(activeInterval.from * 1000);

        return format(activeIntervalDate, 'yyyy-MM-dd');
    }, [activeInterval]);

    const onDateChange = useCallback(
        (date: string) => {
            fields.deliveryInterval.setValue({ type: 'interval', ...dates[date][0] });
        },
        [dates, fields.deliveryInterval]
    );

    const dateRadioProps: RadioGroupProps = {
        orientation: 'horizontal',
        defaultValue: defaultDate,
        onChange: onDateChange,
    };
    const radioGroupState = useRadioGroupState(dateRadioProps);

    const { radioGroupProps } = useRadioGroup(dateRadioProps, radioGroupState);

    const onSubmit = useCallback(async () => {
        revalidate();

        const values = await formInstance.submit();

        if (values) {
            propOnSubmit?.(values.deliveryInterval);
        }
    }, [formInstance, propOnSubmit, revalidate]);

    const maxIntervals = useMemo(() => Math.max(...entries(dates).map(([_, date]) => date.length)), [dates]);

    return (
        <VStack className={styles.root}>
            {deliveryMethod === OrderDeliveryMethods.COURIER ? (
                <CourierTimeSlotPicker
                    radioGroupProps={radioGroupProps}
                    radioGroupState={radioGroupState}
                    dates={dates}
                    formId={formId}
                    maxIntervals={maxIntervals}
                    ozonDeliveryInterval={ozonDeliveryInterval}
                />
            ) : (
                <PickupPointTimeSlotPicker radioGroupState={radioGroupState} intervals={intervals} formId={formId} />
            )}

            <VStack className={styles.button}>
                <DrawerBottomFixed>
                    <DrawerButton onClick={onSubmit}>Сохранить</DrawerButton>
                </DrawerBottomFixed>
            </VStack>
        </VStack>
    );
};
